import type {
    ExploreChecklistItem,
    ExploreFilters,
    ExploreLevel,
    ExploreOption,
} from './types'

const LEVEL_ORDER: ExploreLevel[] = ['combo', 'industry', 'major', 'middle']
const COMMON_INDUSTRY = '전체 업종 공통'

export const EMPTY_EXPLORE_FILTERS: ExploreFilters = {
    combo: '',
    industry: '',
    major: '',
    middle: '',
    query: '',
}

/** 체크리스트 항목이 주어진 탐색 단계에서 갖는 표시 값을 반환한다. */
export function getItemLevelValue(
    item: ExploreChecklistItem,
    level: ExploreLevel
): string {
    if (level === 'combo') return getComboValue(item)
    if (level === 'industry') return item.industry || COMMON_INDUSTRY
    if (level === 'major') return item.majorCategory || '대분류 없음'
    return item.middleCategory || '중분류 없음'
}

/** 현재 단계 이전의 선택 조건만 적용해 다음 단계 후보를 계산한다. */
export function getExploreOptions(
    level: ExploreLevel,
    filters: ExploreFilters,
    items: ExploreChecklistItem[]
): ExploreOption[] {
    // 백엔드가 원본 카탈로그(시트) 순서로 항목을 주므로, 그 첫 등장 순서를 그대로
    // 유지한다. 가나다(locale) 정렬을 하면 시트 번호 순서가 깨진다.
    const optionValues = uniqueValues(
        items
            .filter((item) => matchesBeforeLevel(item, level, filters))
            .map((item) => getItemLevelValue(item, level))
    )

    return optionValues.map((value) => ({
        value,
        label: level === 'combo' ? getComboLabel(value) : value,
        count: items.filter(
            (item) =>
                matchesBeforeLevel(item, level, filters) &&
                getItemLevelValue(item, level) === value
        ).length,
    }))
}

/** 모든 선택 조건과 검색어를 적용해 체크리스트 목록을 좁힌다. */
export function filterExploreItems(
    items: ExploreChecklistItem[],
    filters: ExploreFilters
): ExploreChecklistItem[] {
    const query = filters.query.trim()

    return items.filter((item) => {
        if (!matchesAllLevels(item, filters)) return false
        if (!query) return true
        return getSearchText(item).includes(query)
    })
}

/** 화면 상단 경로에 표시할 현재 선택값을 만든다. */
export function getExploreBreadcrumb(filters: ExploreFilters): string[] {
    return [
        filters.combo ? getComboLabel(filters.combo) : '',
        filters.industry,
        filters.major,
        filters.middle,
    ].filter(Boolean)
}

function matchesBeforeLevel(
    item: ExploreChecklistItem,
    level: ExploreLevel,
    filters: ExploreFilters
): boolean {
    const levelIndex = LEVEL_ORDER.indexOf(level)
    return LEVEL_ORDER.slice(0, levelIndex).every((previousLevel) =>
        matchesLevel(item, previousLevel, filters[previousLevel])
    )
}

function matchesAllLevels(
    item: ExploreChecklistItem,
    filters: ExploreFilters
): boolean {
    return LEVEL_ORDER.every((level) =>
        matchesLevel(item, level, filters[level])
    )
}

function matchesLevel(
    item: ExploreChecklistItem,
    level: ExploreLevel,
    value: string
): boolean {
    return !value || getItemLevelValue(item, level) === value
}

function uniqueValues(values: string[]): string[] {
    return [...new Set(values)]
}

function getSearchText(item: ExploreChecklistItem): string {
    return [
        item.id,
        item.part,
        item.classification,
        item.assessmentType,
        item.industry,
        item.scale,
        item.majorCategory,
        item.middleCategory,
        item.itemNumber,
        item.title,
        item.scoringScale,
        item.guidance,
        item.dataType,
        item.scoringLogic,
        ...item.fields.flatMap((field) => [
            field.fieldId,
            field.nameKo,
            field.unit,
            field.dataType,
            field.category,
            field.description,
            field.threshold,
            field.question,
        ]),
    ]
        .filter(Boolean)
        .join(' ')
}

function getComboValue(item: ExploreChecklistItem): string {
    return `${item.scale}::${item.classification}::${item.assessmentType ?? ''}`
}

function getComboLabel(value: string): string {
    const [scale, classification, assessmentType] = value.split('::')
    return [getScaleLabel(scale), classification || '분류 없음', assessmentType]
        .filter(Boolean)
        .join(' · ')
}

function getScaleLabel(scale: string): string {
    // scale 값은 이미 한글(일반/소규모)이라 그대로 표시한다.
    return scale
}
